/**
 * Sondeo de la foto de disponibilidad: ~3 s con jitter (±20 %), pausa con la pestaña oculta,
 * reanuda al volver y se retrasa con backoff exponencial si hay errores. Sin DOM: el entorno se inyecta.
 */

export const POLL_BASE_MS = 3000;
export const POLL_JITTER = 0.2;
export const POLL_MAX_MS = 30_000;

/** Próximo intervalo: base * 2^errores (tope 30 s), con jitter simétrico. `rand` devuelve [0, 1). */
export function nextDelay(errors: number, rand: () => number, base = POLL_BASE_MS, jitter = POLL_JITTER, max = POLL_MAX_MS): number {
  const backoff = errors > 0 ? Math.min(base * 2 ** Math.min(errors, 6), max) : base;
  return Math.round(backoff * (1 + (rand() * 2 - 1) * jitter));
}

export interface PollEnv {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
  visible: () => boolean;
  rand: () => number;
}

export class Poller {
  private timer: unknown = null;
  private running = false;
  private again = false;
  private stopped = true;
  errors = 0;

  constructor(
    private readonly run: () => Promise<void>,
    private readonly env: PollEnv,
    /** se invoca tras cada ciclo: `ok` y número de errores seguidos */
    private readonly onResult?: (ok: boolean, errors: number) => void,
    private readonly base = POLL_BASE_MS,
  ) {}

  start(): void {
    this.stopped = false;
    this.schedule();
  }

  stop(): void {
    this.stopped = true;
    this.clear();
  }

  /** La pestaña vuelve a ser visible (o hay red de nuevo): refresca ya. */
  resume(): void {
    if (this.stopped) return;
    void this.cycle();
  }

  /** Pide una lectura inmediata (p. ej. tras un conflicto). */
  poke(): void {
    if (this.stopped) return;
    void this.cycle();
  }

  private clear(): void {
    if (this.timer !== null) this.env.clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    this.clear();
    if (this.stopped) return;
    this.timer = this.env.setTimeout(() => {
      this.timer = null;
      if (!this.env.visible()) return; // pausado: `resume()` lo retomará
      void this.cycle();
    }, nextDelay(this.errors, this.env.rand, this.base));
  }

  private async cycle(): Promise<void> {
    if (this.stopped) return;
    if (this.running) {
      this.again = true;
      return;
    }
    this.clear();
    this.running = true;
    let ok = true;
    try {
      await this.run();
      this.errors = 0;
    } catch {
      ok = false;
      this.errors++;
    } finally {
      this.running = false;
    }
    if (this.stopped) return;
    this.onResult?.(ok, this.errors);
    if (this.again) {
      this.again = false;
      void this.cycle();
      return;
    }
    if (this.env.visible()) this.schedule();
  }
}
